import type { ProductSizeGuide as ProductSizeGuideSchema } from '../../../../amplienceTypes/schemas/imported/product-size-guide-schema';
import type { SizeGuideProps } from '../../sizeGuide/SizeGuide';
import type { SizeMeasurementsProps } from '../../sizeGuide/SizeMeasurements';
import type { ReactElement } from 'react';

import dynamic from 'next/dynamic';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useDataLayerContext } from '../../../../utilities/context/static/DataLayerContext';
import { usePDPContext } from '../../../../utilities/context/static/PDPContext';
import { useStaticContext } from '../../../../utilities/context/static/StaticContext';
import { useHashChanged } from '../../../../utilities/hooks';
import { Dialog } from '../../../shared/core/dialog/Dialog';
import { colors } from '../../../shared/core/styles';
import { FULLSTORY_EVENTS } from '~/utilities/constants/fullstoryEvents';
import { trackFullstoryEvent } from '~/utilities/helpers/syncGlobalObject';

const SizeMeasurements = dynamic<SizeGuideProps & SizeMeasurementsProps>(
  () => import('../../sizeGuide/SizeMeasurements').then(mod => mod.SizeMeasurements),
  { ssr: false }
);
const SizeGuide = dynamic<SizeGuideProps>(
  () => import('../../sizeGuide/SizeGuide').then(mod => mod.SizeGuide),
  { ssr: false }
);

const S = {
  SizeGuideLinkLabel: styled.button`
    line-height: 1.4;
    text-transform: capitalize;
    text-decoration: underline;
    font-weight: ${({ theme }) => theme.variables.font.weight.regular};
    font-size: ${({ theme }) => theme.font.size[2]};
    color: ${({ theme }) => theme.colors.content.hyperlink};

    &:hover,
    &:focus {
      color: ${({ theme }) => theme.colors.content.component.buttons.tertiary.hover};
    }
  `,
};

export const SizeGuideBlock = ({
  labelProps,
}: {
  labelProps: ProductSizeGuideSchema;
}): ReactElement => {
  const {
    configuration: { enableSizeGuideProductMeasurements },
  } = useStaticContext();

  const { sizeGuideLabelV2 } = labelProps;

  const { pushToDataLayer } = useDataLayerContext();
  const {
    product: { code, shouldRequestSizeMeasurements },
  } = usePDPContext();

  const [showSizeMeasurements, setShowSizeMeasurements] = useState(
    enableSizeGuideProductMeasurements && shouldRequestSizeMeasurements
  );

  const handleNoSizeMeasurementsData = () => {
    // To resolve race condition caused
    setTimeout(() => {
      setShowSizeMeasurements(false);
    }, 0);
  };

  const dataLayerEventData = useMemo(
    () => ({
      events: {
        category: 'size guide',
        action: 'open',
        label: undefined,
      },
      product: {
        materialNumber: code,
      },
      event: 'sizeguide-open',
    }),
    [code]
  );

  const { hashMatched, setHashMatched } = useHashChanged(
    ['#size-guide', '#measurement-guide'],
    dataLayerEventData
  );

  const handleOpenSizeGuideModal = useCallback(
    (e: { preventDefault: () => void }) => {
      e.preventDefault();
      setHashMatched(true);
      pushToDataLayer(dataLayerEventData);
      trackFullstoryEvent(FULLSTORY_EVENTS.sizeGuideOpened, { product_code: code });
    },
    [code, dataLayerEventData, pushToDataLayer, setHashMatched]
  );

  const handleCloseSizeGuideModal = useCallback(() => {
    setHashMatched(false);
  }, [setHashMatched]);

  return (
    <>
      <>
        {sizeGuideLabelV2 && (
          <S.SizeGuideLinkLabel
            aria-haspopup="dialog"
            onClick={handleOpenSizeGuideModal}
            data-testid="size-guide-link"
            data-cs-capture=""
          >
            {sizeGuideLabelV2 || ''}
          </S.SizeGuideLinkLabel>
        )}
      </>

      {hashMatched && (
        <Dialog
          ariaLabel="size guide"
          onClose={handleCloseSizeGuideModal}
          position={showSizeMeasurements ? 'center' : 'top'}
          overlayColor={colors.DIALOG_DARK_GREY}
        >
          {showSizeMeasurements ? (
            <SizeMeasurements
              labelProps={labelProps}
              handleNoSizeMeasurementsData={handleNoSizeMeasurementsData}
            />
          ) : (
            <SizeGuide labelProps={labelProps} />
          )}
        </Dialog>
      )}
    </>
  );
};
